import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, ClickAwayListener, Fade, Popper } from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import { inclusiveGray } from 'shared/utils/inclusiveColors';

const ENTER_DELAY_MS = 200;
const LEAVE_DELAY_MS = 100;

export interface HoverCardWrapperProps {
  onHoverComponent: React.ReactNode;
  children: React.ReactNode;
  offset?: [number, number];
  onOpen?: () => void;
  onSecondTap?: () => void;
  /**
   * Desktop keyboard support: open when the child receives focus (Tab, or
   * Enter/Space on a focused button), close on blur or Escape.
   */
  openOnFocus?: boolean;
}

function resolvePlacement(anchorEl: HTMLElement): 'bottom-start' | 'top-start' {
  const rect = anchorEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const estimatedCardHeight = 420;
  return spaceBelow < estimatedCardHeight && rect.top > estimatedCardHeight
    ? 'top-start'
    : 'bottom-start';
}

export function HoverCardWrapper({
  onHoverComponent,
  children,
  offset = [0, 8],
  onOpen,
  onSecondTap,
  openOnFocus = false,
}: HoverCardWrapperProps): React.ReactElement {
  const isMobile = useIsMobile();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<'bottom-start' | 'top-start'>(
    'bottom-start'
  );
  const enterTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const mobileOpenRef = useRef(false);
  const popperContentRef = useRef<HTMLDivElement>(null);

  const clearTimers = useCallback(() => {
    clearTimeout(enterTimeoutRef.current);
    clearTimeout(leaveTimeoutRef.current);
  }, []);

  const handleOpen = useCallback(
    (element: HTMLElement) => {
      setAnchorEl(element);
      setPlacement(resolvePlacement(element));
      setOpen(true);
      onOpen?.();
    },
    [onOpen]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    mobileOpenRef.current = false;
  }, []);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (isMobile) {
        return;
      }
      clearTimers();
      const target = event.currentTarget;
      enterTimeoutRef.current = setTimeout(() => {
        handleOpen(target);
      }, ENTER_DELAY_MS);
    },
    [clearTimers, handleOpen, isMobile]
  );

  const handleMouseLeave = useCallback(() => {
    if (isMobile) {
      return;
    }
    clearTimers();
    leaveTimeoutRef.current = setTimeout(() => {
      handleClose();
    }, LEAVE_DELAY_MS);
  }, [clearTimers, handleClose, isMobile]);

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      if (!openOnFocus || isMobile) {
        return;
      }
      clearTimers();
      handleOpen(event.currentTarget);
    },
    [clearTimers, handleOpen, isMobile, openOnFocus]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      if (!openOnFocus) {
        return;
      }
      // React blur events bubble through the Popper portal. Moving focus
      // between the trigger and focusable content of the card keeps it open.
      const nextFocused = event.relatedTarget as Node | null;
      if (
        nextFocused &&
        (event.currentTarget.contains(nextFocused) ||
          popperContentRef.current?.contains(nextFocused))
      ) {
        return;
      }
      handleMouseLeave();
    },
    [handleMouseLeave, openOnFocus]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      // Like MUI Tooltip, the preview is non-modal: Escape closes it without
      // stopping propagation, so a surrounding dialog still gets the key.
      if (openOnFocus && open && event.key === 'Escape') {
        clearTimers();
        handleClose();
      }
    },
    [clearTimers, handleClose, open, openOnFocus]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!isMobile) {
        // Enter/Space (or a click) on the focused child re-opens a preview
        // that was dismissed with Escape.
        if (openOnFocus && !open) {
          clearTimers();
          handleOpen(event.currentTarget);
        }
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (mobileOpenRef.current) {
        handleClose();
        onSecondTap?.();
        return;
      }
      mobileOpenRef.current = true;
      handleOpen(event.currentTarget);
    },
    [clearTimers, handleClose, handleOpen, isMobile, onSecondTap, open, openOnFocus]
  );

  useEffect(() => () => clearTimers(), [clearTimers]);

  const modifiers = [
    {
      name: 'offset',
      options: { offset },
    },
    {
      name: 'flip',
      enabled: true,
    },
    {
      name: 'preventOverflow',
      options: { padding: 8 },
    },
  ];

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (isMobile && open) {
          handleClose();
        }
      }}
    >
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        sx={{
          display: 'inline-flex',
          maxWidth: '100%',
          minWidth: 0,
          cursor: 'pointer',
        }}
      >
        {children}
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement={placement}
          transition
          modifiers={modifiers}
          sx={{
            zIndex: 1300,
            backgroundColor: inclusiveGray[50],
          }}
        >
          {({ TransitionProps }) => (
            <Fade
              {...TransitionProps}
              timeout={{ enter: 100, exit: 100 }}
              easing={{ enter: 'ease-out', exit: 'ease-in' }}
            >
              <Box
                onMouseEnter={() => {
                  if (!isMobile) {
                    clearTimers();
                  }
                }}
                onMouseLeave={handleMouseLeave}
                ref={popperContentRef}
                sx={{ transition: 'opacity 0.2s ease-out' }}
              >
                {onHoverComponent}
              </Box>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
